import { Injectable } from '@angular/core';
import { ICdwSaveComponentStateService } from '@cdw-ae/common-ui-components-search-grid';
import { SelectionCacheService } from './selection-cache.service';

const SELECTION_FIELDS = [
  'selectedIds',
  'selectAll',
  'filterBySelected',
  'promptSelectAll',
  'multiSelectCheckboxChecked',
  'multiSelectCheckboxIndeterminate',
] as const;

/**
 * localStorage-backed implementation of the grids' state persistence port.
 *
 * `<unknown>` is deliberate, not a placeholder: one instance serves every
 * grid, and the split between `SELECTION_FIELDS` (routed to the in-memory
 * selection cache) and everything else (serialized to localStorage) is the
 * only thing this service knows about the blob. It is shape-agnostic by
 * design, so it has nothing to do with a narrower `T`.
 */
@Injectable({
  providedIn: 'root',
})
export class ComponentStateService
  implements ICdwSaveComponentStateService<unknown>
{
  private readonly STORAGE_PREFIX = 'cdw_component_state_';

  constructor(private selectionCache: SelectionCacheService) {}

  public saveComponentState(componentId: string, state: unknown): void {
    try {
      const key = this.getStorageKey(componentId);
      const stateRecord = state as Record<string, unknown>;

      const selections: Record<string, unknown> = {};
      const persistable: Record<string, unknown> = {};

      for (const [k, v] of Object.entries(stateRecord)) {
        if ((SELECTION_FIELDS as readonly string[]).includes(k)) {
          selections[k] = v;
        } else {
          persistable[k] = v;
        }
      }

      this.selectionCache.save(componentId, selections);
      localStorage.setItem(key, JSON.stringify(persistable));
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }

  /**
   * Returns `unknown` — the value is `JSON.parse` output from localStorage
   * merged with the selection cache, so callers must validate before use.
   */
  public retrieveComponentState(componentId: string): unknown {
    try {
      const key = this.getStorageKey(componentId);
      const serialized = localStorage.getItem(key);
      const selections = this.selectionCache.get(componentId);

      if (serialized) {
        const parsed = JSON.parse(serialized) as Record<string, unknown>;
        return selections ? { ...parsed, ...selections } : parsed;
      }

      return selections || undefined;
    } catch {
      return undefined;
    }
  }

  public clearComponentState(componentId: string): void {
    try {
      const key = this.getStorageKey(componentId);
      localStorage.removeItem(key);
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }

  private getStorageKey(componentId: string): string {
    return `${this.STORAGE_PREFIX}${componentId}`;
  }
}
